import React, { useEffect, useMemo, useState } from 'react'
import { EmailIcon, UserPlusIcon, UsersIcon } from '../../../Common/SvgIcons/WorkflowIcons'
import PathBreadcrumb from '../../../../components/PathBreadcrumb/PathBreadcrumb'
import { useLocation, useNavigate } from 'react-router-dom'
import useFetch from '../../../../components/useFetch'
import { Button } from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import CreateUser from '../tenant/Organizations/CreateUser'
import { Skeleton } from '@mui/material'
import TitleHeader from '../../../project/header/TitleHeader'
import './Users.scss'

const formatTitleCase = (value) => {
    if (!value) return '-'
    return String(value)
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase())
}

const isActiveStatus = (status) => String(status || '').toLowerCase() === 'active'

export const Users = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const [search, setSearch] = React.useState('')
    const [create, setCreate] = useState(false)

    const tenantId = location.state?.id
    const tenantName = location.state?.tenantName

    useEffect(() => {
        if (location.state?.openCreate) {
            setCreate(true)
        }
    }, [location.state?.openCreate])

    const url = tenantId
        ? `users?filter=tenant_id=${tenantId},status=active`
        : 'users?filter=status=active'

    const { data, loading, refetch } = useFetch(url, 'get')

    const users = data?.results || []
    const hasUsers = users.length > 0
    const hasSearch = Boolean(search.trim())

    const filteredUsers = useMemo(() => {
        const q = search.trim().toLowerCase()
        if (!q) return users
        return users.filter((u) => (
            u.name?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q) ||
            u.roles?.[0]?.toLowerCase().includes(q)
        ))
    }, [users, search])

    const goToTenant = () => {
        if (!tenantId) {
            navigate(-1)
            return
        }
        navigate('/admin/tenant/details', { state: { id: tenantId } })
    }

    const breadcrumbItems = [
        { label: 'Admin', onClick: () => navigate('/admin') },
        { label: 'Tenants', onClick: () => navigate('/admin/tenants') },
        ...(tenantId
            ? [{ label: tenantName || 'Tenant', onClick: goToTenant }]
            : []),
        { label: 'Users' },
    ]

    const openUserProfile = (user) => {
        const isSelf = String(user?.id) === String(localStorage.getItem('user_id'))
        navigate(isSelf ? '/user-profile' : '/admin/user-details', {
            state: {
                id: user.id,
                user,
                viewFrom: isSelf ? 'profile_dashboard' : 'user_details',
            },
        })
    }

    const renderToolbar = () => (
        <div className="users-toolbar">
            <div className="users-toolbar__search">
                <FontAwesomeIcon className="plain-button2 text-secondary" icon={faSearch} />
                <input
                    type="search"
                    placeholder="Search members by name or email..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    aria-label="Search members by name or email"
                />
            </div>

            <div className="users-toolbar__action">
                <Button
                    color="primary"
                    className="py-2 btn-click d-j-a mt-0"
                    onClick={() => setCreate(true)}
                >
                    <UserPlusIcon className="wf-icon text-white" /> Add User
                </Button>
            </div>
        </div>
    )

    const renderEmptyState = () => {
        if (!hasUsers) {
            return (
                <div className="users-empty users-empty--catalog">
                    <span className="users-empty__icon">
                        <UsersIcon className="wf-icon text-primary" />
                    </span>
                    <h5>No users in this tenant yet</h5>
                    <p>
                        Invite the first member to set up access, roles, and workspace permissions
                        for this organization.
                    </p>
                    <Button
                        color="primary"
                        className="py-2 btn-click d-j-a mt-0"
                        onClick={() => setCreate(true)}
                    >
                        <UserPlusIcon className="wf-icon text-white" /> Add User
                    </Button>
                </div>
            )
        }

        return (
            <div className="users-empty">
                <h5>No matching users found</h5>
                <p>Try adjusting your search.</p>
                <Button color="primary" onClick={() => setSearch('')}>
                    Clear Search
                </Button>
            </div>
        )
    }

    if (loading) {
        return (
            <>
                <div className="mainmenu de-width pt-3 users-page">
                    <PathBreadcrumb
                        onBack={tenantId ? goToTenant : () => navigate('/admin')}
                        items={breadcrumbItems}
                    />

                    <div className="d-a justify-content-between pt-1 gap-3">
                        <div className="d-a gap-3 flex-fill">
                            <Skeleton
                                variant="rounded"
                                width={40}
                                height={40}
                                className="landing-page-main-icon flex-shrink-0"
                            />
                            <div className="title-header__copy">
                                <Skeleton variant="text" width={220} height={36} />
                                <Skeleton variant="text" width={280} height={22} className="mt-1" />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex d-md-none my-4 w-100">
                        <Skeleton variant="rounded" width="100%" height={42} />
                    </div>

                    <div className="row g-3 d-flex d-md-none">
                        {[...Array(4)].map((_, idx) => (
                            <div key={idx} className="col-12">
                                <div className="container-card h-100 p-3">
                                    <div className="d-flex gap-3 align-items-start">
                                        <Skeleton variant="rounded" width={28} height={28} />
                                        <div className="w-100">
                                            <Skeleton variant="text" width="40%" height={22} />
                                            <Skeleton variant="text" width="65%" height={18} />
                                            <Skeleton variant="text" width="50%" height={18} className="mt-2" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="users-table-wrap mt-3 container-card-right d-none d-md-block">
                        <div className="mb-3">
                            <Skeleton variant="rounded" width="100%" height={42} />
                        </div>
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Joined On</th>
                                    <th className="th-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...Array(5)].map((_, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <div className="users-table-primary">
                                                <Skeleton variant="rounded" width={28} height={28} />
                                                <div style={{ width: '100%' }}>
                                                    <Skeleton variant="text" width="40%" height={18} />
                                                    <Skeleton variant="text" width="55%" height={14} />
                                                </div>
                                            </div>
                                        </td>
                                        <td><Skeleton variant="text" width={80} height={18} /></td>
                                        <td><Skeleton variant="text" width={60} height={18} /></td>
                                        <td><Skeleton variant="text" width={90} height={18} /></td>
                                        <td className="actions"><Skeleton variant="text" width={60} height={18} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="mobile-view-bottom"></div>
            </>
        )
    }

    return (
        <>
            <div className="mainmenu de-width pt-3 users-page">
                <PathBreadcrumb
                    onBack={tenantId ? goToTenant : () => navigate('/admin')}
                    items={breadcrumbItems}
                />

                <div className="d-a justify-content-between pt-1 gap-3">
                    <TitleHeader
                        Icon={UsersIcon}
                        title1="Tenant"
                        title2=" User Management"
                        subtext={
                            hasUsers
                                ? 'Add new members to your organization or manage existing user permissions and access levels.'
                                : 'Provision the first user for this tenant to unlock roles, access, and workspace collaboration.'
                        }
                    />
                </div>

                {(hasUsers || hasSearch) && (
                    <div className="d-md-none mt-3 w-100">
                        {renderToolbar()}
                    </div>
                )}

                <div className="row g-3 d-flex d-md-none mt-3">
                    {filteredUsers.length === 0 ? (
                        <div className="col-12">{renderEmptyState()}</div>
                    ) : (
                        filteredUsers.map((u) => {
                            const firstLetter = u.name?.charAt(0)?.toUpperCase() || 'U'
                            const role = formatTitleCase(u.roles?.[0] || 'User')
                            const active = isActiveStatus(u.status)

                            return (
                                <article
                                    key={u.id}
                                    className="col-12 cursor-pointer"
                                    onClick={() => openUserProfile(u)}
                                >
                                    <div className="container-card users-card h-100 p-3">
                                        <div className="users-card__row">
                                            <div className="users-table-avatar">{firstLetter}</div>
                                            <div className="users-card__body">
                                                <div className="users-card__top">
                                                    <h6 className="users-card__name">{u.name}</h6>
                                                    <span className={`users-status ${active ? 'is-active' : 'is-inactive'}`}>
                                                        {formatTitleCase(u.status)}
                                                    </span>
                                                </div>

                                                <div className="users-card__email">
                                                    <EmailIcon className="wf-icon-small" />
                                                    <small>{u.email}</small>
                                                </div>

                                                <div className="users-card__meta">
                                                    <span className="users-role">{role}</span>
                                                    <small>
                                                        Joined {new Date(u.created_at).toLocaleDateString()}
                                                    </small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            )
                        })
                    )}
                </div>

                <div className="users-table-wrap mt-4 container-card-right d-none d-md-block">
                    {(hasUsers || hasSearch) && (
                        <div className="mb-3">{renderToolbar()}</div>
                    )}

                    {filteredUsers.length === 0 ? (
                        renderEmptyState()
                    ) : (
                        <table className="users-table">
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Role</th>
                                    <th>Status</th>
                                    <th>Joined On</th>
                                    <th className="th-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredUsers.map((u) => {
                                    const firstLetter = u.name?.charAt(0)?.toUpperCase() || 'U'
                                    const role = formatTitleCase(u.roles?.[0] || 'User')
                                    const active = isActiveStatus(u.status)

                                    return (
                                        <tr key={u.id}>
                                            <td>
                                                <div
                                                    className="users-table-primary cursor-pointer"
                                                    onClick={() => openUserProfile(u)}
                                                >
                                                    <span className="users-table-avatar">{firstLetter}</span>
                                                    <div>
                                                        <strong>{u.name}</strong>
                                                        <span>{u.email}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="users-role">{role}</span>
                                            </td>
                                            <td>
                                                <span className={`users-status ${active ? 'is-active' : 'is-inactive'}`}>
                                                    {formatTitleCase(u.status)}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="users-date">
                                                    {new Date(u.created_at).toLocaleDateString()}
                                                </span>
                                            </td>
                                            <td className="actions">
                                                <button
                                                    className="users-action-btn"
                                                    type="button"
                                                    onClick={() => openUserProfile(u)}
                                                >
                                                    Manage
                                                </button>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {create && (
                <CreateUser
                    tenantId={tenantId}
                    setCreate={setCreate}
                    onSuccess={() => {
                        refetch()
                    }}
                />
            )}

            <div className="mobile-view-bottom"></div>
        </>
    )
}
